import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/CreateCategoryModal.scss';
import { updateCategory, deleteCategory, ApiResponse } from '../utils/api';
import { useTheme } from '../hooks/useTheme';
import logger from '../utils/logger';
import Notification from './Notification';
import { validateCategoryName } from '../utils/validation';
import { NOTIFICATION_DURATION } from '../constants/notifications';

interface EditCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryUpdated: () => void;
  categoryName: string;
  categoryEmoji: string;
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
  isOpen,
  onClose,
  onCategoryUpdated,
  categoryName,
  categoryEmoji
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [newCategoryName, setNewCategoryName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setNewCategoryName(categoryName);
      setEmoji(categoryEmoji || '📝');
      setError(null);
      setShowDeleteConfirm(false);
    }
  }, [isOpen, categoryName, categoryEmoji]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewCategoryName(e.target.value);
    setError(null);
  };

  const handleEmojiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Разрешаем пустую строку или один-два символа (эмодзи может занимать до 2 UTF-16 единиц)
    if (value === '' || value.length <= 2) {
      // Проверка на наличие обычных текстовых символов (буквы, цифры, пунктуация, пробелы)
      const hasCommonCharacters = /[a-zA-Z0-9.,!?;:'"()\[\]{}\s]/.test(value);
      
      // Если нет обычных символов, считаем это эмодзи
      if (!hasCommonCharacters) {
        setEmoji(value);
      }
    }
  };

  const handleSubmit = async () => {
    const validationError = validateCategoryName(newCategoryName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await updateCategory(categoryName, newCategoryName.trim(), emoji || '📝');

      if (response.success) {
        logger.log('Категория успешно обновлена:', newCategoryName);
        
        // После успешного обновления перенаправляем на новый URL
        if (categoryName !== newCategoryName) {
          // Если изменилось имя категории, переходим на новый URL
          navigate(`/dictionary/category/${newCategoryName}?emoji=${encodeURIComponent(emoji || '📝')}`, { replace: true });
        } else {
          // Если имя не изменилось, просто обновляем URL с новым emoji
          navigate(`/dictionary/category/${categoryName}?emoji=${encodeURIComponent(emoji || '📝')}`, { replace: true });
        }
        
        onCategoryUpdated();
        onClose();
      } else {
        setError(response.error || 'Не удалось обновить категорию');
        logger.error('Ошибка при обновлении категории:', response.error);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(`Произошла ошибка при обновлении категории: ${errorMsg}`);
      logger.error('Исключение при обновлении категории:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      const response = await deleteCategory(categoryName);

      if (response.success) {
        logger.log('Категория успешно удалена:', categoryName);
        navigate('/dictionary', { replace: true });
        onClose();
      } else {
        setError(response.error || 'Не удалось удалить категорию');
        logger.error('Ошибка при удалении категории:', response.error);
        setShowDeleteConfirm(false);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(`Произошла ошибка при удалении категории: ${errorMsg}`);
      logger.error('Исключение при удалении категории:', err);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {error && (
        <Notification
          message={error}
          onClose={() => setError(null)}
          duration={NOTIFICATION_DURATION}
        />
      )}
      <div className="create-category-modal-overlay" onClick={onClose}>
        <div className="create-category-modal" onClick={(e) => e.stopPropagation()}>
          <h2>Редактирование категории</h2>
          
          {!showDeleteConfirm ? (
            <>
              <div className="input-container">
                <label htmlFor="category-emoji">Эмодзи:</label>
                <input
                  id="category-emoji"
                  type="text"
                  value={emoji}
                  onChange={handleEmojiChange}
                  className="emoji-input"
                  maxLength={2}
                  inputMode="none"
                  aria-label="Выберите эмодзи"
                  title="Введите эмодзи"
                />
              </div>
              
              <div className="input-container">
                <label htmlFor="category-name">Название категории:</label>
                <input
                  key={`input-${animationKey}`}
                  id="category-name"
                  type="text"
                  value={newCategoryName}
                  onChange={handleNameChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Введите название категории..."
                  className="name-input"
                  autoFocus
                  disabled={isLoading}
                />
              </div>
              
              <div className="delete-category-section">
                <button 
                  className="delete-category-button" 
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isLoading}
                >
                  <span className="delete-icon">🗑️</span>
                  Удалить категорию
                </button>
              </div>

              <div className="modal-footer">
                <button 
                  className="cancel-button" 
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Отмена
                </button>
                <button 
                  className="create-button" 
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </>
          ) : (
            <div className="delete-confirm">
              <h3>Удалить категорию "{categoryName}"?</h3>
              <p>Это действие нельзя отменить. Все слова, связанные с этой категорией, будут откреплены от нее, но останутся в словаре.</p>
              
              <div className="confirm-buttons">
                <button 
                  className="cancel-delete-button" 
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Отмена
                </button>
                <button 
                  className="confirm-delete-button" 
                  onClick={handleDeleteCategory}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Удаление...' : 'Удалить'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EditCategoryModal; 